import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { cn } from "@/lib/cn";
import { ArrowLeft, Building2, Network, Save, CheckCircle2, AlertCircle, Info, Lock, History, UserPlus, Star, RefreshCw } from "lucide-react";
import { toast } from "react-toastify";
import { showActionErrorFromUnknown } from "@/lib/feedback";
import { profileService, ProfileResponse, LegalEntityHierarchy } from "@/services/profileService";
import { apiClient } from "@/services/apiClient";
import { legalEntitiesService } from "@/services/legalEntitiesService";
import { branchesService } from "@/services/branchesService";
import { integrationsService } from "@/services/integrationsService";
import {
  crmGatewaySyncService,
  fetchBranchEntityStatuses,
  aggregateBranchStatus,
  HISTORICAL_SYNC_ENTITY_TYPES,
  REAPIT_HISTORICAL_SYNC_ENTITY_TYPES,
} from "@/services/crmGatewaySyncService";
import { useKeycloak } from "@/modules/auth/context/KeycloakContext";
import { useSettingsStore } from "@/store/settingsStore";
import { useProfileStore } from "@/store/profileStore";
import { useCrmMappingSyncStore } from "@/store/crmMappingSyncStore";
import { Button } from "@/components/atoms/Button";
import { Input } from "@/components/atoms/Input";
import { Tooltip } from "@/components/atoms/Tooltip";
import { useMemo } from "react";
import { HistoricalSyncModal } from "./HistoricalSyncModal";
import { OnboardUsersModal } from "./OnboardUsersModal";

const REF_LOCKED_MESSAGE =
  "This CRM reference is locked. Please contact the support team to change it.";

const BRANCH_RESYNC_COOLDOWN_MS = 5 * 60 * 1000;
const BRANCH_RESYNC_COOLDOWN_STORAGE_KEY = "crm-branch-resync-cooldown-until";

function readBranchResyncCooldownUntil(): Record<string, number> {
  try {
    const raw = localStorage.getItem(BRANCH_RESYNC_COOLDOWN_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, number>;
    const now = Date.now();
    const active: Record<string, number> = {};
    for (const [id, until] of Object.entries(parsed)) {
      if (until > now) active[id] = until;
    }
    return active;
  } catch {
    return {};
  }
}

function writeBranchResyncCooldownUntil(next: Record<string, number>) {
  const now = Date.now();
  const active: Record<string, number> = {};
  for (const [id, until] of Object.entries(next)) {
    if (until > now) active[id] = until;
  }
  if (Object.keys(active).length === 0) {
    localStorage.removeItem(BRANCH_RESYNC_COOLDOWN_STORAGE_KEY);
  } else {
    localStorage.setItem(BRANCH_RESYNC_COOLDOWN_STORAGE_KEY, JSON.stringify(active));
  }
}

// Statuses for which a branch's historical sync is actively running — the
// History icon is disabled while a branch is in one of these. PAUSE is
// deliberately excluded so the operator can still open the modal to see the
// paused state (they just can't start a new sync from there).
const ACTIVE_HISTORICAL_SYNC_STATUSES = new Set(["IN_PROGRESS", "RUNNING", "QUEUED"]);

const notifyLocked = () => toast.info(REF_LOCKED_MESSAGE, { autoClose: 4000 });

/**
 * Deep-link highlight (`?branch=<id>`, e.g. from Settings → Leads Processing):
 * once the page has settled, it glides the branch into view, then blinks an
 * accent ring twice around the one control the user has to act on. The blink
 * starts only when the scroll has finished, so it is always seen.
 */
/** Pause after the list renders so the glide starts from a settled page. */
const SCROLL_SETTLE_MS = 200;
/** Matches the `bridge-flash-*` animation length in theme/global.css. */
const FLASH_DURATION_MS = 1400;
/** Which control on a branch row the deep link points at. */
type FlashTarget = "ref" | "action";
type BranchFlash = { branchId: string; target: FlashTarget; on: boolean };
/** Two blinks of an accent ring + soft halo in the platform blue. */
const FLASH_RING_CLASS = "bridge-flash-ring";

interface CrmIntegrationDetailProps {
  crmId: string;
  onBack: () => void;
}

export const CrmIntegrationDetail: React.FC<CrmIntegrationDetailProps> = ({ crmId, onBack }) => {
  const { keycloak } = useKeycloak();
  const email = keycloak?.tokenParsed?.email as string | undefined;
  const { legalEntityId, setLegalEntity } = useSettingsStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const focusBranchId = searchParams.get("branch");
  const [branchFlash, setBranchFlash] = useState<BranchFlash | null>(null);

  const [profile, setProfile] = useState<ProfileResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Local state for refs
  const [leRefs, setLeRefs] = useState<Record<string, string>>({});
  const [branchRefs, setBranchRefs] = useState<Record<string, string>>({});

  // Ids whose ref was already populated on the server when this page loaded.
  // These are immutable from the UI — users must contact support to change.
  const [lockedLeIds, setLockedLeIds] = useState<Set<string>>(new Set());
  const [lockedBranchIds, setLockedBranchIds] = useState<Set<string>>(new Set());
  // Aggregate historical-sync status per branch (COMPLETED, PAUSE, IN_PROGRESS,
  // …). Fetched on demand — no continuous polling — and kept in sync by the
  // modal, so the History icons and the modal share one source of truth.
  const [branchHistoricalStatus, setBranchHistoricalStatus] = useState<Record<string, string>>({});
  const [branchResyncCooldownUntil, setBranchResyncCooldownUntil] = useState<Record<string, number>>(
    readBranchResyncCooldownUntil,
  );

  // Historical-sync modal target: which branch is being replayed.
  const [historicalSyncTarget, setHistoricalSyncTarget] = useState<{ id: string; name: string } | null>(null);

  // Property media replay is supported only by Reapit. Keep the same list for
  // status checks, aggregation, and the modal so other CRMs neither display nor
  // request those entity types.
  const historicalSyncEntityTypes = useMemo(
    () =>
      crmId.trim().toUpperCase() === "REAPIT"
        ? REAPIT_HISTORICAL_SYNC_ENTITY_TYPES
        : HISTORICAL_SYNC_ENTITY_TYPES,
    [crmId],
  );

  // Onboard-users modal target: which LE's users we're pushing to Keycloak.
  // Carries the LE's direct branches so the modal can list them with their
  // NEGOTIATOR sync status.
  const [onboardUsersTarget, setOnboardUsersTarget] = useState<{
    id: string;
    name: string;
    branches: { id: string; name: string }[];
  } | null>(null);

  const selectedEntity = useMemo(() => {
    if (!profile || !legalEntityId) return null;
    for (const le of profile.legalEntities) {
      if (findEntity([le], legalEntityId)) {
        return le;
      }
    }
    return null;
  }, [profile, legalEntityId]);

  const displayEntities = useMemo(() => {
    if (selectedEntity) return [selectedEntity];
    return profile?.legalEntities ?? [];
  }, [profile, selectedEntity]);

  // Branches whose historical sync is actively running — drives the disabled
  // state of the History icons. Derived from the shared status map.
  const historicalSyncingBranchIds = useMemo(() => {
    const ids = new Set<string>();
    for (const [id, status] of Object.entries(branchHistoricalStatus)) {
      if (ACTIVE_HISTORICAL_SYNC_STATUSES.has(status)) ids.add(id);
    }
    return ids;
  }, [branchHistoricalStatus]);

  // The single true root legal entity for this org's hierarchy (e.g.
  // "Hunters"), resolved server-side — sub-entities like "Handsworth" are
  // NOT roots of their own even though they show a Legal Entity Ref field.
  // Only this one entity's ref is required before anything else can save.
  const [rootLegalEntityId, setRootLegalEntityId] = useState<string | null>(null);

  useEffect(() => {
    const anchorId = profile?.legalEntities[0]?.id;
    if (!anchorId) return;
    apiClient
      .get("/profile/hierarchy/root-legal-entity", { params: { legalEntityId: anchorId } })
      .then((res) => setRootLegalEntityId(res?.data?.rootLegalEntityId ?? anchorId))
      .catch((err) => console.error("root-legal-entity error", err));
  }, [profile]);

  const hasMissingRootRefs = useMemo(() => {
    if (!rootLegalEntityId) return false;
    return !leRefs[rootLegalEntityId]?.trim();
  }, [rootLegalEntityId, leRefs]);

  // True only when there is an unlocked ref with a value — i.e. something
  // handleSave would actually write. Walks the same tree as the save.
  const hasPendingChanges = useMemo(() => {
    if (!profile) return false;
    const walk = (entities: LegalEntityHierarchy[]): boolean =>
      entities.some(
        (le) =>
          (!lockedLeIds.has(le.id) && !!leRefs[le.id]?.trim()) ||
          le.branches.some((b) => !lockedBranchIds.has(b.id) && !!branchRefs[b.id]?.trim()) ||
          (le.subEntities ? walk(le.subEntities) : false),
      );
    return walk(profile.legalEntities);
  }, [profile, leRefs, branchRefs, lockedLeIds, lockedBranchIds]);

  const localDuplicateErrors = useMemo(() => {
    const counts = new Map<string, number>();
    if (profile) {
      const walk = (entities: LegalEntityHierarchy[]) => {
        entities.forEach(le => {
          const lv = leRefs[le.id]?.trim().toLowerCase();
          if (lv) counts.set(lv, (counts.get(lv) || 0) + 1);
          le.branches.forEach(b => {
            const bv = branchRefs[b.id]?.trim().toLowerCase();
            if (bv) counts.set(bv, (counts.get(bv) || 0) + 1);
          });
          if (le.subEntities) walk(le.subEntities);
        });
      };
      walk(displayEntities);
    }
    return counts;
  }, [profile, displayEntities, leRefs, branchRefs]);

  const getLocalDuplicateError = (val?: string) => {
    const trimmed = val?.trim().toLowerCase();
    if (trimmed && (localDuplicateErrors.get(trimmed) || 0) > 1) {
      return "This CRM reference is duplicated in the current form.";
    }
    return null;
  };
  const hasLocalDuplicates = Array.from(localDuplicateErrors.values()).some(c => c > 1);

  // Gate: every legal entity with more than one branch must have a primary
  // branch chosen before its references can be integrated. Single-branch LEs
  // are auto-marked server-side, so they never trip this.
  const hasUnselectedPrimaryBranch = useMemo(() => {
    if (!profile) return false;
    let missing = false;
    const walk = (entities: LegalEntityHierarchy[]) => {
      entities.forEach(le => {
        if (le.branches.length > 1 && !le.branches.some(b => b.isPrimary)) {
          missing = true;
        }
        if (le.subEntities) walk(le.subEntities);
      });
    };
    walk(displayEntities);
    return missing;
  }, [profile, displayEntities]);



  /**
   * Walks the profile tree and hydrates the form state from whatever's
   * persisted for this CRM. Extracted so both the initial mount load AND
   * the post-save refresh use the exact same derivation — otherwise the
   * locked sets drift after a save and the Sync Historical button on a
   * just-saved branch only shows up after a manual page reload.
   */
  const hydrateFromProfile = (p: ProfileResponse) => {
    setProfile(p);

    const lr: Record<string, string> = {};
    const br: Record<string, string> = {};
    const lockedLes = new Set<string>();
    const lockedBrs = new Set<string>();
    const collectRefs = (entities: LegalEntityHierarchy[]) => {
      entities.forEach((le) => {
        if (le.sourceCrm === crmId && le.agencyRef) {
          lr[le.id] = le.agencyRef;
          lockedLes.add(le.id);
        }
        le.branches.forEach((b) => {
          if (b.sourceCrm === crmId && b.agencyRef) {
            br[b.id] = b.agencyRef;
            lockedBrs.add(b.id);
          }
        });
        if (le.subEntities) collectRefs(le.subEntities);
      });
    };
    collectRefs(p.legalEntities);

    setLeRefs(lr);
    setBranchRefs(br);
    setLockedLeIds(lockedLes);
    setLockedBranchIds(lockedBrs);
  };

  useEffect(() => {
    if (!email) return;
    profileService
      .get(email)
      .then((p) => {
        hydrateFromProfile(p);
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load hierarchy");
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, crmId]);

  // Resolve a `?branch=` deep link once the hierarchy has loaded. The param is
  // dropped as soon as it is handled so a refresh or Back doesn't replay it.
  useEffect(() => {
    if (loading || !profile || !focusBranchId) return;
    const clearParam = () =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("branch");
          return next;
        },
        { replace: true },
      );

    const owner = findBranchOwner(profile.legalEntities, focusBranchId);
    if (!owner) {
      clearParam();
      return;
    }
    // The page only lists the header's legal entity. When the linked branch
    // lives under another one, switch to it; this effect re-runs once the list
    // shows it.
    if (!displayEntities.some((le) => findEntity([le], owner.id))) {
      setLegalEntity(owner.id, owner.name);
      return;
    }
    clearParam();
    // No saved ref yet → the Branch Ref input is what needs filling in.
    // Ref saved → the row's sync action (Resync / History) is the next step.
    setBranchFlash({
      branchId: focusBranchId,
      target: lockedBranchIds.has(focusBranchId) ? "action" : "ref",
      on: false,
    });
  }, [loading, profile, focusBranchId, displayEntities, lockedBranchIds, setLegalEntity, setSearchParams]);

  const flashBranchId = branchFlash?.branchId;
  const flashTarget = branchFlash?.target;
  useEffect(() => {
    if (!flashBranchId) return;
    let cancelScroll: (() => void) | undefined;
    let hideTimer: ReturnType<typeof setTimeout> | undefined;

    const settleTimer = setTimeout(() => {
      const row = document.querySelector<HTMLElement>(
        `[data-branch-row="${CSS.escape(flashBranchId)}"]`,
      );
      if (!row) {
        setBranchFlash(null);
        return;
      }
      cancelScroll = glideIntoView(row, () => {
        setBranchFlash((f) => (f ? { ...f, on: true } : f));
        // Put the cursor in the ref field so the user can type straight away.
        if (flashTarget === "ref") row.querySelector("input")?.focus({ preventScroll: true });
        hideTimer = setTimeout(() => setBranchFlash(null), FLASH_DURATION_MS);
      });
    }, SCROLL_SETTLE_MS);

    return () => {
      clearTimeout(settleTimer);
      cancelScroll?.();
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, [flashBranchId, flashTarget]);

  // Fetch historical-sync status ONCE when the branch list is ready, so the
  // History icons reflect in-progress branches on load. There is no continuous
  // polling: status is refreshed on demand from the modal (on open and via its
  // Refresh button) through handleBranchStatusChange below.
  useEffect(() => {
    if (!profile) return;

    const branchIds: string[] = [];
    const walk = (nodes: LegalEntityHierarchy[]) => {
      nodes.forEach((le) => {
        le.branches?.forEach((b) => {
          if (b?.id && lockedBranchIds.has(b.id) && b.branchSynced) branchIds.push(b.id);
        });
        if (le.subEntities?.length) walk(le.subEntities);
      });
    };
    walk(displayEntities);

    if (branchIds.length === 0) {
      setBranchHistoricalStatus({});
      return;
    }

    let cancelled = false;
    // /sync-status requires entityType, so status is fetched per entity (four
    // calls) and collapsed into one aggregate status per branch.
    fetchBranchEntityStatuses(branchIds, { entityTypes: historicalSyncEntityTypes })
      .then((byBranch) => {
        if (cancelled) return;
        const map: Record<string, string> = {};
        for (const id of branchIds) {
          map[id] = aggregateBranchStatus(byBranch[id] ?? {}, historicalSyncEntityTypes);
        }
        setBranchHistoricalStatus(map);
      })
      .catch(() => {
        // Best-effort; icons keep their prior state.
      });

    return () => {
      cancelled = true;
    };
  }, [profile, displayEntities, lockedBranchIds, historicalSyncEntityTypes]);

  // The modal reports fresh status here (on open, manual Refresh, or right after
  // queuing a sync) so the History icon stays in sync without a page reload.
  const handleBranchStatusChange = (branchId: string, status: string) => {
    setBranchHistoricalStatus((prev) => ({ ...prev, [branchId]: status.toUpperCase() }));
  };

  useEffect(() => {
    writeBranchResyncCooldownUntil(branchResyncCooldownUntil);
  }, [branchResyncCooldownUntil]);

  useEffect(() => {
    const pending = Object.values(branchResyncCooldownUntil).filter((until) => until > Date.now());
    if (pending.length === 0) return;

    const timer = window.setTimeout(() => {
      setBranchResyncCooldownUntil((prev) => {
        const now = Date.now();
        const next: Record<string, number> = {};
        let changed = false;
        for (const [id, until] of Object.entries(prev)) {
          if (until > now) next[id] = until;
          else changed = true;
        }
        return changed ? next : prev;
      });
    }, Math.min(...pending.map((until) => until - Date.now())) + 50);

    return () => window.clearTimeout(timer);
  }, [branchResyncCooldownUntil]);

  const handleSetPrimary = async (branchId: string) => {
    if (!email) return;
    try {
      await branchesService.setPrimary(branchId);
      profileService.invalidate(email);
      const fresh = await profileService.refresh(email);
      hydrateFromProfile(fresh);
      useProfileStore.setState({ profileData: fresh, isLoading: false, error: null });
    } catch {
      showActionErrorFromUnknown(null, "Failed to set primary branch");
    }
  };

  const handleResyncBranch = async (branchId: string) => {
    if (!email) return;
    if (Date.now() < (branchResyncCooldownUntil[branchId] ?? 0)) return;
    try {
      await crmGatewaySyncService.replay({ branchId, entityType: "BRANCH" as any });
      setBranchResyncCooldownUntil((prev) => ({
        ...prev,
        [branchId]: Date.now() + BRANCH_RESYNC_COOLDOWN_MS,
      }));
      toast.success("Branch sync queued");
      profileService.invalidate(email);
      const fresh = await profileService.refresh(email);
      hydrateFromProfile(fresh);
      useProfileStore.getState().refreshProfile(email);
    } catch {
      showActionErrorFromUnknown(null, "Failed to queue branch resync");
    }
  };

  const handleSave = async () => {
    if (!email || !profile) return;
    if (hasMissingRootRefs || hasLocalDuplicates) {
      setError("Please fix the highlighted errors before saving.");
      return;
    }
    if (hasUnselectedPrimaryBranch) {
      setError("Select a primary branch for each legal entity with multiple branches before saving.");
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const checkPromises: Promise<any>[] = [];
      const checkRecursive = (entities: LegalEntityHierarchy[]) => {
        entities.forEach(le => {
          if (leRefs[le.id]?.trim() && !lockedLeIds.has(le.id)) {
            checkPromises.push(integrationsService.checkAgencyRef({
              sourceCrm: crmId,
              ref: leRefs[le.id].trim(),
              excludeKind: "LE",
              excludeId: le.id,
              legalEntityId: le.id
            }).then(res => {
              if (res.duplicate) {
                throw new Error(`Already used by ${res.conflict?.kind === "LE" ? "Legal Entity" : "Branch"} "${res.conflict?.name}". Each CRM reference must be unique.`);
              }
            }));
          }
          le.branches.forEach(branch => {
            if (branchRefs[branch.id]?.trim() && !lockedBranchIds.has(branch.id)) {
              checkPromises.push(integrationsService.checkAgencyRef({
                sourceCrm: crmId,
                ref: branchRefs[branch.id].trim(),
                excludeKind: "BR",
                excludeId: branch.id,
                legalEntityId: le.id
              }).then(res => {
                if (res.duplicate) {
                  throw new Error(`Already used by ${res.conflict?.kind === "LE" ? "Legal Entity" : "Branch"} "${res.conflict?.name}". Each CRM reference must be unique.`);
                }
              }));
            }
          });
          if (le.subEntities) checkRecursive(le.subEntities);
        });
      };

      if (profile) {
        checkRecursive(profile.legalEntities);
      }

      await Promise.all(checkPromises);

      const promises: Promise<any>[] = [];

      const saveRecursive = (entities: LegalEntityHierarchy[]) => {
        entities.forEach(le => {
          // Skip ids that were locked at load — they're immutable from the UI.
          if (leRefs[le.id] !== undefined && !lockedLeIds.has(le.id)) {
            promises.push(legalEntitiesService.update(le.id, {
              sourceCrm: crmId,
              agencyRef: leRefs[le.id]
            }));
          }
          le.branches.forEach(branch => {
            if (branchRefs[branch.id] !== undefined && !lockedBranchIds.has(branch.id)) {
              promises.push(branchesService.update(branch.id, {
                sourceCrm: crmId,
                agencyRef: branchRefs[branch.id]
              }));
            }
          });
          if (le.subEntities) saveRecursive(le.subEntities);
        });
      };

      if (profile) {
        saveRecursive(profile.legalEntities);
      }

      if (promises.length === 0) {
        setError("Please enter at least one reference to save.");
        setSaving(false);
        return;
      }

      await Promise.all(promises);
      profileService.invalidate(email);
      useCrmMappingSyncStore.getState().bumpCrmMappingEpoch();

      try {
        const fresh = await profileService.refresh(email);
        hydrateFromProfile(fresh);
        useProfileStore.getState().refreshProfile(email);
      } catch {
        // Save succeeded; worst case the operator sees stale state until
        // they manually refresh.
      }

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      // Atlas returns 409 AGENCY_REF_CONFLICT when a save would create a
      // duplicate — surface the server's reason verbatim so the operator
      // knows which entity holds the conflicting ref.
      const code = err?.response?.data?.code;
      const message = err?.response?.data?.message ?? err?.message;
      if (code === "AGENCY_REF_CONFLICT") {
        setError(message ?? "This CRM reference is already used by another entity.");
      } else {
        setError(message ?? "Failed to save configuration");
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-bridge-accent"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-bridge-bg">
      <header className="px-8 py-4 bg-white border-b border-bridge-border flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-bridge-bg rounded-full text-bridge-muted transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="text-2xl">{crmId === "reapit" ? "🏠" : "☁️"}</div>
            <div>
              <h1 className="text-xl font-bold text-bridge-fg capitalize">{crmId} Configuration</h1>
              <p className="text-xs text-bridge-muted mt-0.5">Map your organizational hierarchy to {crmId} references.</p>
            </div>
          </div>
        </div>
        <IconTooltip
          content={
            hasMissingRootRefs
              ? "Fill Root Legal Entity references before saving"
              : hasLocalDuplicates
                ? "Fix duplicate CRM references in the form before saving"
                : hasUnselectedPrimaryBranch
                  ? "Select a primary branch for each legal entity before saving references"
                  : "No changes to save"
          }
          disabled={!(hasMissingRootRefs || hasLocalDuplicates || hasUnselectedPrimaryBranch || !hasPendingChanges)}
          className="inline-flex"
        >
          <Button
            onClick={handleSave}
            loading={saving}
            disabled={hasMissingRootRefs || hasLocalDuplicates || hasUnselectedPrimaryBranch || !hasPendingChanges}
            className="bg-bridge-accent hover:bg-bridge-accent/90 text-white rounded-xl px-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4 mr-2" />
            Save Configuration
          </Button>
        </IconTooltip>
      </header>

      <main className="flex-1 overflow-auto p-8">
        <div className="max-w-6xl mx-auto space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border border-red-100 text-red-600 text-sm rounded-2xl flex items-center gap-3">
              <AlertCircle className="w-5 h-5 shrink-0" />
              {error}
            </div>
          )}
          {success && (
            <div className="p-4 bg-green-50 border border-green-100 text-green-600 text-sm rounded-2xl flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 shrink-0" />
              Configuration saved successfully!
            </div>
          )}

          <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 flex gap-3 text-blue-800 text-sm">
            <Info className="w-5 h-5 shrink-0 text-blue-400" />
            <div className="space-y-1">
              <p>
                Enter the <strong>Legal Entity Ref</strong> for each Legal Entity and the <strong>Branch Ref</strong> for each corresponding branch.
                Once saved, these entities will be linked to {crmId}.
              </p>
              <p>
                After saving new references, the branch will begin syncing. You can click the <strong>Refresh</strong> icon next to a branch to trigger a manual sync, or the <strong>History</strong> icon to manage historical syncs once the branch is ready. If you don't see these updates immediately, please refresh the screen.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            {displayEntities.map((le) => (
              <div key={le.id} className="bg-white rounded-2xl border border-bridge-border overflow-hidden shadow-sm">
                <div className="px-6 py-4 bg-slate-50 border-b border-bridge-border flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-bridge-accent" />
                    <span className="font-bold text-bridge-fg">{le.name}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="text-[10px] font-bold text-bridge-muted uppercase tracking-wider whitespace-nowrap">Legal Entity Ref</label>
                    <LockableRefInput
                      value={leRefs[le.id] || ""}
                      locked={lockedLeIds.has(le.id)}
                      onChange={(v) => setLeRefs({ ...leRefs, [le.id]: v })}
                      error={le.id === rootLegalEntityId && !leRefs[le.id]?.trim() ? "Root Legal Entity Ref must be filled" : undefined}
                    />
                    {/* Onboard-users slot: present only on integrated LEs.
                        Width matches branch rows' Sync Historical slot so
                        columns stay aligned. */}
                    <div className="w-10 h-10 flex items-center justify-center shrink-0">
                      {lockedLeIds.has(le.id) ? (
                        <IconTooltip
                          content="Onboard users for this legal entity to Keycloak"
                          className="inline-flex items-center justify-center w-10 h-10"
                        >
                          <button
                            type="button"
                            onClick={() =>
                              setOnboardUsersTarget({
                                id: le.id,
                                name: le.name,
                                branches: collectSubtreeBranches(le),
                              })
                            }
                            aria-label={`Onboard users for ${le.name}`}
                            className="w-10 h-10 flex items-center justify-center rounded-lg border border-bridge-border bg-white text-bridge-accent hover:bg-bridge-accent hover:text-white hover:border-bridge-accent transition-colors shadow-sm"
                          >
                            <UserPlus className="w-4 h-4" />
                          </button>
                        </IconTooltip>
                      ) : (
                        <span aria-hidden="true" />
                      )}
                    </div>
                  </div>
                </div>
                <div className="divide-y divide-bridge-border/50">
                  <HierarchyNode
                    le={le}
                    depth={0}
                    leRefs={leRefs}
                    setLeRefs={(r) => setLeRefs({ ...leRefs, ...r })}
                    branchRefs={branchRefs}
                    setBranchRefs={(r) => setBranchRefs({ ...branchRefs, ...r })}
                    lockedLeIds={lockedLeIds}
                    lockedBranchIds={lockedBranchIds}
                    historicalSyncingBranchIds={historicalSyncingBranchIds}
                    branchResyncCooldownUntil={branchResyncCooldownUntil}

                    getLocalDuplicateError={getLocalDuplicateError}
                    onOpenHistoricalSync={(id, name) => setHistoricalSyncTarget({ id, name })}
                    onOpenOnboardUsers={(leId, leName, brs) =>
                      setOnboardUsersTarget({ id: leId, name: leName, branches: brs })
                    }
                    onSetPrimary={handleSetPrimary}
                    onResyncBranch={handleResyncBranch}
                    branchFlash={branchFlash}
                  />
                  {le.branches.length === 0 && (!le.subEntities || le.subEntities.length === 0) && (
                    <div className="px-14 py-4 text-xs text-bridge-muted italic">No branches or sub-entities configured.</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      <HistoricalSyncModal
        open={!!historicalSyncTarget}
        branchId={historicalSyncTarget?.id ?? ""}
        branchName={historicalSyncTarget?.name ?? ""}
        entityTypes={historicalSyncEntityTypes}
        status={historicalSyncTarget ? branchHistoricalStatus[historicalSyncTarget.id] : undefined}
        onStatusChange={handleBranchStatusChange}
        onClose={() => setHistoricalSyncTarget(null)}
      />

      <OnboardUsersModal
        open={!!onboardUsersTarget}
        legalEntityId={onboardUsersTarget?.id ?? ""}
        legalEntityName={onboardUsersTarget?.name ?? ""}
        branches={onboardUsersTarget?.branches ?? []}
        onClose={() => setOnboardUsersTarget(null)}
      />
    </div>
  );
};

const HierarchyNode: React.FC<{
  le: LegalEntityHierarchy;
  depth: number;
  leRefs: Record<string, string>;
  setLeRefs: (r: Record<string, string>) => void;
  branchRefs: Record<string, string>;
  setBranchRefs: (r: Record<string, string>) => void;
  lockedLeIds: Set<string>;
  lockedBranchIds: Set<string>;
  historicalSyncingBranchIds: Set<string>;
  branchResyncCooldownUntil: Record<string, number>;

  getLocalDuplicateError: (val?: string) => string | null;
  onOpenHistoricalSync: (branchId: string, branchName: string) => void;
  onOpenOnboardUsers: (
    legalEntityId: string,
    legalEntityName: string,
    branches: { id: string; name: string }[],
  ) => void;
  onSetPrimary: (branchId: string) => void;
  onResyncBranch: (branchId: string) => void;
  /** Deep-link highlight currently playing, if any. */
  branchFlash: BranchFlash | null;
}> = ({
  le,
  depth,
  leRefs,
  setLeRefs,
  branchRefs,
  setBranchRefs,
  lockedLeIds,
  lockedBranchIds,
  historicalSyncingBranchIds,
  branchResyncCooldownUntil,

  getLocalDuplicateError,
  onOpenHistoricalSync,
  onOpenOnboardUsers,
  onSetPrimary,
  onResyncBranch,
  branchFlash,
}) => {

    return (
      <>
        {/* If depth > 0, we render the LE as a row (like a branch) */}
        {depth > 0 && (
          <div className="px-6 py-4 flex items-center justify-between hover:bg-slate-50/50 transition-colors border-t border-bridge-border/30">
            <div className="flex items-center gap-3" style={{ paddingLeft: `${depth * 2}rem` }}>
              <Building2 className="w-4 h-4 text-bridge-accent/60" />
              <span className="text-sm font-semibold text-bridge-fg">{le.name}</span>
            </div>
            <div className="flex items-center gap-4">
              <label className="text-[10px] font-bold text-bridge-muted uppercase tracking-wider whitespace-nowrap">Legal Entity Ref</label>
              <LockableRefInput
                value={leRefs[le.id] || ""}
                locked={lockedLeIds.has(le.id)}
                onChange={(v) => setLeRefs({ ...leRefs, [le.id]: v })}
                error={getLocalDuplicateError(leRefs[le.id])}
              />
              {/* Onboard-users slot for sub-entities — same placement as the
                top-level LE header so alignment with branch rows holds. */}
              <div className="w-10 h-10 flex items-center justify-center shrink-0">
                {lockedLeIds.has(le.id) ? (
                  <IconTooltip
                    content="Onboard users for this legal entity to Keycloak"
                    className="inline-flex items-center justify-center w-10 h-10"
                  >
                    <button
                      type="button"
                      onClick={() =>
                        onOpenOnboardUsers(le.id, le.name, collectSubtreeBranches(le))
                      }
                      aria-label={`Onboard users for ${le.name}`}
                      className="w-10 h-10 flex items-center justify-center rounded-lg border border-bridge-border bg-white text-bridge-accent hover:bg-bridge-accent hover:text-white hover:border-bridge-accent transition-colors shadow-sm"
                    >
                      <UserPlus className="w-4 h-4" />
                    </button>
                  </IconTooltip>
                ) : (
                  <span aria-hidden="true" />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Branches of this LE */}
        {le.branches?.map(branch => {
          const flashOn = branchFlash?.on && branchFlash.branchId === branch.id;
          const flashRef = flashOn && branchFlash?.target === "ref";
          const flashAction = flashOn && branchFlash?.target === "action";
          return (
          <div
            key={branch.id}
            data-branch-row={branch.id}
            className={cn(
              "px-6 py-4 flex items-center justify-between transition-colors duration-300 border-t border-bridge-border/30",
              flashOn ? "bridge-flash-bg" : "hover:bg-slate-50/50",
            )}
          >
            <div className="flex items-center gap-3" style={{ paddingLeft: `${(depth + 1) * 2}rem` }}>
              <Network className="w-4 h-4 text-bridge-muted" />
              <span className="text-sm font-medium text-bridge-fg">{branch?.name}</span>
              {(le.branches?.length ?? 0) > 1 && (
                <IconTooltip
                  content={
                    branch?.isPrimary
                      ? "Primary branch for this legal entity"
                      : "Set as primary branch"
                  }
                  className="inline-flex"
                >
                  <button
                    type="button"
                    onClick={() => { if (!branch?.isPrimary) onSetPrimary(branch.id); }}
                    disabled={!!branch?.isPrimary}
                    className={`flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full border transition-all ${branch.isPrimary
                        ? "bg-amber-50 text-amber-600 border-amber-200 cursor-default"
                        : "bg-white text-bridge-muted border-bridge-border hover:text-amber-500 hover:border-amber-200"
                      }`}
                  >
                    <Star className={`w-3 h-3 ${branch?.isPrimary ? "fill-amber-400 text-amber-500" : ""}`} />
                    {branch?.isPrimary ? "Primary" : "Set primary"}
                  </button>
                </IconTooltip>
              )}
            </div>
            <div className="flex items-center gap-4">
              <label className="text-[10px] font-bold text-bridge-muted uppercase tracking-wider whitespace-nowrap">Branch Ref</label>
              <div className={cn("rounded-lg", flashRef && FLASH_RING_CLASS)}>
                <LockableRefInput
                  value={branchRefs[branch?.id] || ""}
                  locked={lockedBranchIds.has(branch?.id)}
                  onChange={(v) => setBranchRefs({ ...branchRefs, [branch.id]: v })}
                  error={getLocalDuplicateError(branchRefs[branch?.id])}
                />
              </div>
              {/* Fixed-width action slot so all rows align regardless of
                whether the branch has been integrated yet. Locked rows
                render the icon button; the rest render an invisible
                placeholder of the same footprint. */}
              <div
                className={cn(
                  "w-10 h-10 flex items-center justify-center shrink-0 rounded-lg",
                  flashAction && FLASH_RING_CLASS,
                )}
              >
                {lockedBranchIds.has(branch?.id) ? (
                  branch?.branchSynced ? (
                    <HistoricalSyncIconButton
                      inProgress={historicalSyncingBranchIds.has(branch?.id)}
                      onClick={() => onOpenHistoricalSync(branch.id, branch?.name ?? "")}
                      branchName={branch?.name ?? ""}
                    />
                  ) : (
                    <BranchSyncActionIconButton
                      inProgress={Date.now() < (branchResyncCooldownUntil[branch?.id] ?? 0)}
                      onClick={() => onResyncBranch(branch.id)}
                      title="Resync branch configuration"
                      ariaLabel={`Resync branch configuration for ${branch?.name ?? ""}`}
                    >
                      <RefreshCw className="w-4 h-4" />
                    </BranchSyncActionIconButton>
                  )
                ) : (
                  <span aria-hidden="true" />
                )}
              </div>
            </div>
          </div>
          );
        })}

        {/* Sub-entities of this LE */}
        {le.subEntities?.map(sub => (
          <HierarchyNode
            key={sub.id}
            le={sub}
            depth={depth + 1}
            leRefs={leRefs}
            setLeRefs={setLeRefs}
            branchRefs={branchRefs}
            setBranchRefs={setBranchRefs}
            onOpenHistoricalSync={onOpenHistoricalSync}
            onOpenOnboardUsers={onOpenOnboardUsers}
            onSetPrimary={onSetPrimary}
            onResyncBranch={onResyncBranch}
            branchFlash={branchFlash}
            lockedLeIds={lockedLeIds}
            lockedBranchIds={lockedBranchIds}
            historicalSyncingBranchIds={historicalSyncingBranchIds}
            branchResyncCooldownUntil={branchResyncCooldownUntil}

            getLocalDuplicateError={getLocalDuplicateError}
          />
        ))}
      </>
    );
  };

function IconTooltip({
  content,
  children,
  className = "inline-flex items-center justify-center",
  disabled = false,
}: {
  content: string;
  children: React.ReactElement;
  className?: string;
  disabled?: boolean;
}) {
  return (
    <Tooltip content={content} side="left" disabled={disabled}>
      <span className={className}>{children}</span>
    </Tooltip>
  );
}

function BranchSyncActionIconButton({
  inProgress,
  onClick,
  title,
  ariaLabel,
  children,
}: {
  inProgress: boolean;
  onClick: () => void;
  title: string;
  ariaLabel: string;
  children: React.ReactNode;
}) {
  const button = (
    <button
      type="button"
      disabled={inProgress}
      onClick={onClick}
      aria-label={ariaLabel}
      className={`w-10 h-10 flex items-center justify-center rounded-lg border border-bridge-border bg-white text-bridge-accent shadow-sm transition-colors ${
        inProgress
          ? "opacity-50 cursor-not-allowed"
          : "hover:bg-bridge-accent hover:text-white hover:border-bridge-accent"
      }`}
    >
      {children}
    </button>
  );

  return (
    <IconTooltip
      content={inProgress ? "Syncing in progress" : title}
      className="inline-flex items-center justify-center w-10 h-10"
    >
      {button}
    </IconTooltip>
    );
}

function HistoricalSyncIconButton({
  inProgress,
  onClick,
  branchName,
}: {
  inProgress: boolean;
  onClick: () => void;
  branchName: string;
}) {
  return (
    <BranchSyncActionIconButton
      inProgress={inProgress}
      onClick={onClick}
      title="Sync historical data for this branch"
      ariaLabel={`Sync historical data for ${branchName}`}
    >
      <History className="w-4 h-4" />
    </BranchSyncActionIconButton>
  );
}

/**
 * Renders an editable {@link Input} when {@code locked} is false, or a
 * read-only badge with a lock icon when true. Any interaction with a locked
 * value (click, focus) surfaces a toast directing the user to support.
 */
const LockableRefInput: React.FC<{
  value: string;
  locked: boolean;
  onChange: (v: string) => void;
  /** When set, the input renders with red border + a small message below. */
  error?: string | null;
}> = ({ value, locked, onChange, error }) => {
  if (locked) {
    return (
      <IconTooltip content={REF_LOCKED_MESSAGE} className="inline-flex w-96">
        <button
          type="button"
          onClick={notifyLocked}
          className="h-10 w-full inline-flex items-center justify-between gap-2 px-3 bg-slate-100 border border-bridge-border rounded-lg text-sm text-bridge-fg cursor-not-allowed hover:bg-slate-200 transition-colors"
        >
          <span className="truncate font-mono">{value || "—"}</span>
          <Lock className="w-4 h-4 text-bridge-muted shrink-0" />
        </button>
      </IconTooltip>
    );
  }
  return (
    <div className="flex flex-col gap-1 w-96">
      <Input
        placeholder="e.g. ABC"
        className={`h-10 bg-white shadow-sm ${error ? "border-red-400 focus:border-red-500 ring-1 ring-red-200" : ""}`}
        value={value}
        title={error ?? undefined}
        onChange={(e) => onChange(e.target.value)}
      />
      {error && <div className="text-[11px] text-red-600 leading-tight">{error}</div>}
    </div>
  );
};

/** Nearest ancestor that actually scrolls (here, the page's `<main>`). */
function scrollParentOf(el: HTMLElement): HTMLElement {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const { overflowY } = getComputedStyle(p);
    if ((overflowY === "auto" || overflowY === "scroll") && p.scrollHeight > p.clientHeight) {
      return p;
    }
  }
  return (document.scrollingElement as HTMLElement) ?? document.documentElement;
}

const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);

/**
 * Eased scroll that centres `el` in its scroll container, then calls `onDone`.
 * The browser's own `behavior: "smooth"` has a fixed, fairly abrupt curve and
 * no completion signal; this one slows in and out, scales its duration with
 * distance, and reports when it has landed. Returns a cancel function.
 * Honours reduced-motion by jumping straight there.
 */
function glideIntoView(el: HTMLElement, onDone: () => void): () => void {
  const container = scrollParentOf(el);
  const containerTop =
    container === document.scrollingElement ? 0 : container.getBoundingClientRect().top;
  const rect = el.getBoundingClientRect();
  const maxScroll = container.scrollHeight - container.clientHeight;
  const target = Math.max(
    0,
    Math.min(
      maxScroll,
      container.scrollTop + (rect.top - containerTop) - (container.clientHeight - rect.height) / 2,
    ),
  );
  const start = container.scrollTop;
  const distance = target - start;

  const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  if (reduceMotion || Math.abs(distance) < 2) {
    container.scrollTop = target;
    onDone();
    return () => undefined;
  }

  // ~0.7s for a short hop up to ~1.2s for a long one.
  const duration = Math.min(1200, Math.max(700, 500 + Math.abs(distance) * 0.35));
  let frame = 0;
  let startTime: number | null = null;
  const step = (now: number) => {
    if (startTime === null) startTime = now;
    const t = Math.min(1, (now - startTime) / duration);
    container.scrollTop = start + distance * easeInOutCubic(t);
    if (t < 1) frame = requestAnimationFrame(step);
    else onDone();
  };
  frame = requestAnimationFrame(step);
  return () => cancelAnimationFrame(frame);
}

/** The legal entity (at any depth) that directly owns the branch. */
function findBranchOwner(
  entities: LegalEntityHierarchy[],
  branchId: string,
): LegalEntityHierarchy | null {
  for (const entity of entities) {
    if (entity.branches?.some((b) => b.id === branchId)) return entity;
    const found = findBranchOwner(entity.subEntities ?? [], branchId);
    if (found) return found;
  }
  return null;
}

function findEntity(entities: LegalEntityHierarchy[], id: string): LegalEntityHierarchy | null {
  for (const entity of entities) {
    if (entity.id === id) return entity;
    if (entity.subEntities) {
      const found = findEntity(entity.subEntities, id);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Walks the LE subtree and returns every branch under it (direct + recursive
 * sub-entity branches). Each entry carries its immediate parent LE name so
 * the modal can disambiguate same-named branches across sub-entities.
 */
function collectSubtreeBranches(
  le: LegalEntityHierarchy,
): { id: string; name: string; parentLeName?: string }[] {
  const out: { id: string; name: string; parentLeName?: string }[] = [];
  const walk = (node: LegalEntityHierarchy, isRoot: boolean) => {
    node.branches.forEach((b) => {
      out.push({
        id: b.id,
        name: b.name,
        // Only tag with parent name when the branch comes from a descendant
        // LE — direct branches of the clicked LE don't need a sublabel.
        parentLeName: isRoot ? undefined : node.name,
      });
    });
    node.subEntities?.forEach((sub) => walk(sub, false));
  };
  walk(le, true);
  return out;
}
